/**
 * AEOOnboardingWizard
 *
 * Main page for the AEO Onboarding Wizard.
 * Guides users through 12 steps to prepare a site for AEO content generation.
 *
 * Part of AEO Onboarding Wizard
 * Created: February 7, 2026
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  Play,
  RefreshCw,
  Wand2,
  XCircle,
  CheckCircle,
  ArrowLeft,
  Sparkles,
  Loader2
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// Processing status interface
interface ProcessingStatus {
  isProcessing: boolean;
  enrichment: {
    totalBatches: number;
    completedBatches: number;
    activeBatches: number;
    failedBatches: number;
    percent: number;
  };
  embeddings: {
    totalBatches: number;
    completedBatches: number;
    activeBatches: number;
    percent: number;
  };
  summary: {
    totalProducts: number;
    enrichedProducts: number;
    embeddedProducts: number;
  };
}
import { useAEOOnboardingWizard, WizardStep } from '@/hooks/useAEOOnboardingWizard';
import {
  WizardLayout,
  WizardStepList,
  WizardProgress,
  LLMAgentInsights,
  WebsiteUrlStep,
  FeedImportStep,
  AutoStep,
  DataSourceSelectionStep,
  CrawlProgressStep,
  ResearchConfigStep,
  CategorySelectionStep
} from '@/components/aeo-onboarding';

// Step icons
const STEP_ICONS: Record<string, React.ReactNode> = {
  website_url: <Wand2 className="h-5 w-5" />,
  feed_import: <Sparkles className="h-5 w-5" />
};

// Step descriptions for auto-steps (not including crawl_extract and research_config which have their own components)
// Note: category_selection has its own component (CategorySelectionStep)
const STEP_INFO: Record<string, { title: string; description: string }> = {
  sample_products: {
    title: 'Sample Products',
    description: 'AI samples representative products for context grounding'
  },
  category_discovery: {
    title: 'Discover Categories',
    description: 'AI analyzes products to identify distinct categories'
  },
  category_mapping: {
    title: 'Match to Google Categories',
    description: 'Map products to Google Product Categories for standardization'
  },
  sector_detection: {
    title: 'Detect Sector',
    description: 'AI detects your business sector based on product context'
  },
  // research_config has its own component (ResearchConfigStep)
  persona_research: {
    title: 'Research Buyer Personas',
    description: 'Research buyer personas for each product category'
  },
  benefit_research: {
    title: 'Research Perceived Benefits',
    description: 'Identify the perceived benefits customers seek'
  },
  benefit_mapping: {
    title: 'Map Benefits to Attributes',
    description: 'Connect perceived benefits to measurable product attributes'
  },
  attribute_research: {
    title: 'Research Canonical Attributes',
    description: 'Define canonical attributes for your product categories'
  },
  product_enrichment: {
    title: 'AI Enrich Products',
    description: 'Use AI to extract missing attributes from product data'
  },
  embedding_generation: {
    title: 'Generate Embeddings',
    description: 'Creating semantic embeddings for product search and matching'
  },
  demand_research: {
    title: 'Research Demand',
    description: 'Research market demand for your product categories'
  },
  question_wheel: {
    title: 'Generate Question Wheel',
    description: 'Creating common questions buyers ask about your products'
  },
  research_gaps: {
    title: 'Complete Research Gaps',
    description: 'Identify and complete any remaining research gaps'
  },
  readiness_check: {
    title: 'Final Readiness Check',
    description: 'Assess overall AEO readiness and identify blockers'
  },
  aeo_generation: {
    title: 'Generate AEO Content',
    description: 'Creating AI-enhanced organic content for your products'
  }
};

export function ProductAEOOnboardingWizard() {
  const { selectedSite } = useSite();
  const [stepError, setStepError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const autoExecutedStepRef = useRef<string | null>(null);

  const {
    wizard,
    isLoading,
    error,
    isPolling,
    crawlProgress,
    startWizard,
    cancelWizard,
    executeStep,
    retryStep,
    refresh,
    setWebsiteUrl,
    selectDataSource,
    importFeedUrl,
    importFeedFile,
    startCrawl
  } = useAEOOnboardingWizard({
    siteId: selectedSite?.id || '',
    pollInterval: 3000,
    autoStart: false
  });

  // Map steps to WizardStep format
  const steps: WizardStep[] = (wizard?.steps || []).map(s => ({
    key: s.step_key,
    label: s.label || s.step_key,
    status: s.status,
    order: s.step_order
  }));

  // Get current step info
  const currentStep = wizard?.currentStep;
  const currentStepData = wizard?.steps.find(s => s.step_key === currentStep);

  // Handle step execution for auto steps
  const handleAutoExecute = useCallback(async () => {
    if (!currentStep) return;

    setStepError(null);
    setIsExecuting(true);

    try {
      const result = await executeStep(currentStep);
      if (!result.success && result.reason !== 'already_running') {
        setStepError(result.error || 'Step failed');
      }
    } finally {
      setIsExecuting(false);
    }
  }, [currentStep, executeStep]);

  // Auto-execute certain steps when they become current
  useEffect(() => {
    const autoStartSteps = [
      'crawl_extract',
      'category_selection', // Added 2026-02-18: Auto-execute to load categories, requires user input after
      'sample_products',
      'category_discovery',  // Added 2026-02-18
      'category_mapping',
      'sector_detection',
      'persona_research',
      'benefit_research',
      'benefit_mapping',
      'attribute_research',
      'product_enrichment',
      'embedding_generation',  // Added 2026-02-18
      'demand_research',       // Added 2026-02-18
      'question_wheel',        // Added 2026-02-18
      'research_gaps',
      'readiness_check'
      // aeo_generation intentionally excluded — Content Managers trigger it manually
    ];

    // For category_selection, only auto-execute if pending AND no categories loaded yet
    if (currentStep === 'category_selection') {
      if (
        currentStepData?.status === 'pending' &&
        !isExecuting &&
        !currentStepData?.output_data?.categories
      ) {
        handleAutoExecute();
      }
      return;
    }

    // Detect stale running steps (no progress update for >5 min) — backend likely died
    const isStaleRunning = currentStepData?.status === 'running' &&
      currentStepData?.updated_at &&
      (Date.now() - new Date(currentStepData.updated_at).getTime()) > 5 * 60 * 1000;

    // Reset latch when step changes (new step to auto-execute)
    if (currentStep !== autoExecutedStepRef.current) {
      autoExecutedStepRef.current = null;
    }

    if (
      currentStep &&
      autoStartSteps.includes(currentStep) &&
      (currentStepData?.status === 'pending' || isStaleRunning) &&
      !isExecuting &&
      autoExecutedStepRef.current !== currentStep
    ) {
      autoExecutedStepRef.current = currentStep;
      handleAutoExecute();
    }
  }, [currentStep, currentStepData?.status, currentStepData?.updated_at, currentStepData?.output_data, isExecuting, handleAutoExecute]);

  // Processing status for completed wizard
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const processingPollRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch processing status for completed wizards
  const fetchProcessingStatus = useCallback(async () => {
    if (!selectedSite?.id || wizard?.state !== 'completed') return;

    try {
      const response = await authFetch(`/api/aeo-wizard/site/${selectedSite.id}/processing-status`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProcessingStatus(data.data);
        }
      }
    } catch (err) {
      console.error('[AEOWizard] Error fetching processing status:', err);
    }
  }, [selectedSite?.id, wizard?.state]);

  // Poll processing status when wizard is completed and processing is active
  useEffect(() => {
    if (wizard?.state === 'completed') {
      // Fetch immediately
      fetchProcessingStatus();

      // Then poll every 5 seconds if still processing
      processingPollRef.current = setInterval(() => {
        fetchProcessingStatus();
      }, 5000);
    }

    return () => {
      if (processingPollRef.current) {
        clearInterval(processingPollRef.current);
        processingPollRef.current = null;
      }
    };
  }, [wizard?.state, fetchProcessingStatus]);

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  // No site selected
  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the site selector to start the AEO onboarding wizard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // No wizard - show start screen
  if (!wizard) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-3xl font-bold">AEO Onboarding Wizard</h1>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-6 w-6" />
              Prepare Your Site for AEO
            </CardTitle>
            <CardDescription>
              This wizard will guide you through 12 steps to prepare your site for
              AI-enhanced organic (AEO) content generation. The process uses AI to analyze
              your products, research your market, and set up all required data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">What you'll need:</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Your website URL</li>
                  <li>Product feed (CSV, TSV, or XML)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">What the wizard does:</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Imports and analyzes products</li>
                  <li>Researches buyer personas</li>
                  <li>Enriches product data with AI</li>
                  <li>Validates AEO readiness</li>
                </ul>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button onClick={startWizard} size="lg" className="w-full">
              <Play className="mr-2 h-5 w-5" />
              Start Wizard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wizard completed
  if (wizard.state === 'completed') {
    const readinessStep = wizard.steps.find(s => s.step_key === 'readiness_check');
    const readinessData = readinessStep?.output_data as {
      overallScore?: number;
      status?: string;
      blockers?: Array<{ severity: string; message: string; dimension: string }>;
    } | undefined;

    const completedSteps = wizard.steps.filter(s => s.status === 'completed');
    const totalDurationMs = completedSteps.reduce((sum, s) => sum + (s.duration_ms || 0), 0);

    return (
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-3xl font-bold">AEO Onboarding Complete</h1>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                Wizard Completed Successfully
              </CardTitle>
              <CardDescription>
                All {wizard.progress.total} steps completed for {selectedSite?.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Steps Completed</p>
                  <p className="text-2xl font-bold">{wizard.progress.completed}/{wizard.progress.total}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Duration</p>
                  <p className="text-2xl font-bold">{Math.round(totalDurationMs / 1000)}s</p>
                </div>
                <div>
                  <p className="text-muted-foreground">LLM Cost</p>
                  <p className="text-2xl font-bold">${Number(wizard.totalLlmCostUsd || 0).toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">AEO Readiness</p>
                  <p className="text-2xl font-bold">{readinessData?.overallScore || 0}%</p>
                </div>
              </div>

              {/* Processing Status Banner */}
              {processingStatus?.isProcessing && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    <div>
                      <h4 className="font-medium text-blue-800">Background Processing</h4>
                      <p className="text-sm text-blue-600">
                        AI enrichment and embeddings are being generated. This may improve readiness scores.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-blue-700">Enrichment</span>
                        <span className="text-blue-600">{processingStatus.enrichment.percent}%</span>
                      </div>
                      <Progress value={processingStatus.enrichment.percent} className="h-2" />
                      <p className="text-xs text-blue-600 mt-1">
                        {processingStatus.summary.enrichedProducts} / {processingStatus.summary.totalProducts} products
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-blue-700">Embeddings</span>
                        <span className="text-blue-600">{processingStatus.embeddings.percent}%</span>
                      </div>
                      <Progress value={processingStatus.embeddings.percent} className="h-2" />
                      <p className="text-xs text-blue-600 mt-1">
                        {processingStatus.summary.embeddedProducts} / {processingStatus.summary.totalProducts} products
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Processing Complete Message */}
              {processingStatus &&
                !processingStatus.isProcessing &&
                processingStatus.enrichment.percent === 100 &&
                processingStatus.embeddings.percent === 100 && (
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <h4 className="font-medium text-green-800">Processing Complete</h4>
                      <p className="text-sm text-green-600">
                        All products have been enriched and embedded. Check the AEO Readiness Dashboard for updated scores.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <Button asChild size="lg" className="w-full">
                  <a href="/aeo-readiness">
                    <Sparkles className="mr-2 h-5 w-5" />
                    View AEO Readiness Dashboard
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Blockers Card */}
          {readinessData?.blockers && readinessData.blockers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  Next Steps ({readinessData.blockers.length} blockers)
                </CardTitle>
                <CardDescription>
                  Address these to improve AEO readiness
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {readinessData.blockers.map((blocker, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Badge
                        variant={blocker.severity === 'critical' ? 'destructive' : 'secondary'}
                        className="mt-0.5 text-xs"
                      >
                        {blocker.severity}
                      </Badge>
                      <span className="text-muted-foreground">{blocker.message}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Steps Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Steps Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {wizard.steps.map((step) => (
                <div
                  key={step.step_key}
                  className="flex items-center gap-2 p-2 rounded border text-sm"
                >
                  {step.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                  ) : step.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 shrink-0" />
                  )}
                  <span className="truncate">{step.label}</span>
                  {step.duration_ms && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      {step.duration_ms > 1000
                        ? `${(step.duration_ms / 1000).toFixed(1)}s`
                        : `${step.duration_ms}ms`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button variant="outline" onClick={startWizard}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Start New Wizard
          </Button>
        </div>
      </div>
    );
  }

  // Wizard cancelled/failed
  if (wizard.state === 'cancelled' || wizard.state === 'failed') {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-3xl font-bold">AEO Onboarding Wizard</h1>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-6 w-6" />
              Wizard {wizard.state === 'cancelled' ? 'Cancelled' : 'Failed'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {wizard.state === 'cancelled'
                ? 'The wizard was cancelled. You can start a new wizard to continue.'
                : 'The wizard encountered an error and could not complete.'}
            </p>

            <Button onClick={startWizard}>
              <Play className="mr-2 h-4 w-4" />
              Start New Wizard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active wizard - render wizard UI
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">AEO Onboarding Wizard</h1>
          <p className="text-muted-foreground">
            {selectedSite.name} - Step {wizard.currentStepOrder} of {wizard.progress.total}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isPolling && (
            <Badge variant="outline" className="animate-pulse">
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              Updating...
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={cancelWizard}>
            Cancel
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <WizardLayout
        sidebar={
          <>
            <WizardStepList
              steps={steps}
              currentStep={currentStep || ''}
              onStepClick={(key) => {
                // Allow clicking on completed/failed steps
                console.log('Step clicked:', key);
              }}
            />
            <div className="mt-auto pt-6 border-t">
              <WizardProgress
                completed={wizard.progress.completed}
                total={wizard.progress.total}
                percentage={wizard.progress.percentage}
              />
            </div>
          </>
        }
      >
        <div className="space-y-6">
          {/* Step Content */}
          {currentStep === 'website_url' && (
            <WebsiteUrlStep
              initialUrl={selectedSite?.url || ''}
              onSubmit={async (url) => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await setWebsiteUrl(url);
                  if (!result.success) {
                    setStepError(result.error || 'Failed to set website URL');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting}
              error={stepError}
            />
          )}

          {currentStep === 'data_source_selection' && (
            <DataSourceSelectionStep
              onSelect={async (type) => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await selectDataSource(type);
                  if (!result.success) {
                    setStepError(result.error || 'Failed to select data source');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting}
              error={stepError}
            />
          )}

          {currentStep === 'crawl_extract' && (
            <CrawlProgressStep
              progress={crawlProgress}
              error={stepError || currentStepData?.error_message}
              onRetry={async () => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await startCrawl();
                  if (!result.success) {
                    setStepError(result.error || 'Failed to start crawl');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting || currentStepData?.status === 'running'}
            />
          )}

          {currentStep === 'feed_import' && (
            <FeedImportStep
              onSubmitUrl={async (url, format) => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await importFeedUrl(url, format);
                  if (!result.success) {
                    setStepError(result.error || 'Failed to import feed');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              onSubmitFile={async (file, format) => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await importFeedFile(file, format);
                  if (!result.success) {
                    setStepError(result.error || 'Failed to upload feed');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting || currentStepData?.status === 'running'}
              error={stepError}
              progress={currentStepData?.output_data?.progress}
            />
          )}

          {currentStep === 'category_selection' && (
            currentStepData?.output_data?.categories ? (
              <CategorySelectionStep
                categories={currentStepData.output_data.categories as Array<{
                  categoryId: string;
                  displayName: string;
                  productCount: number;
                  samples: Array<{
                    id: string;
                    product_name: string;
                    brand?: string;
                    image_url?: string;
                    price?: string;
                    currency?: string;
                  }>;
                }>}
                totalProducts={(currentStepData.output_data.totalProducts as number) || 0}
                onSubmit={async (selectedCategories, maxProducts, categoryDiscoveryMode) => {
                  setStepError(null);
                  setIsExecuting(true);
                  try {
                    const result = await executeStep('category_selection', { selectedCategories, maxProducts, categoryDiscoveryMode });
                    if (!result.success) {
                      setStepError(result.error || 'Failed to save category selection');
                    }
                  } finally {
                    setIsExecuting(false);
                  }
                }}
                isLoading={isExecuting}
                error={stepError}
              />
            ) : (
              <AutoStep
                title="Loading Categories"
                description="Analyzing your product catalog to identify categories..."
                status={currentStepData?.status || 'pending'}
                result={undefined}
                error={stepError || currentStepData?.error_message}
                onExecute={handleAutoExecute}
                onRetry={async () => {
                  setStepError(null);
                  setIsExecuting(true);
                  try {
                    const result = await retryStep(currentStep);
                    if (!result.success) {
                      setStepError(result.error || 'Retry failed');
                    }
                  } finally {
                    setIsExecuting(false);
                  }
                }}
                isLoading={isExecuting || currentStepData?.status === 'pending'}
                durationMs={currentStepData?.duration_ms || undefined}
              />
            )
          )}

          {currentStep === 'research_config' && (
            <ResearchConfigStep
              detectedSector={wizard.agentContext?.detectedSector || (wizard.agentContext?.suggestedSector ? { name: wizard.agentContext.suggestedSector, id: 0 } : null)}
              onSubmit={async (config) => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await executeStep('research_config', config);
                  if (!result.success) {
                    setStepError(result.error || 'Failed to save configuration');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting || currentStepData?.status === 'running'}
              error={stepError}
            />
          )}

          {/* Auto Steps */}
          {currentStep && STEP_INFO[currentStep] && (
            <AutoStep
              title={STEP_INFO[currentStep].title}
              description={STEP_INFO[currentStep].description}
              status={currentStepData?.status || 'pending'}
              result={undefined}
              error={stepError || currentStepData?.error_message}
              onExecute={handleAutoExecute}
              onRetry={async () => {
                setStepError(null);
                setIsExecuting(true);
                try {
                  const result = await retryStep(currentStep);
                  if (!result.success) {
                    setStepError(result.error || 'Retry failed');
                  }
                } finally {
                  setIsExecuting(false);
                }
              }}
              isLoading={isExecuting || currentStepData?.status === 'running'}
              durationMs={currentStepData?.duration_ms || undefined}
            />
          )}

          {/* LLM Agent Insights */}
          <LLMAgentInsights
            contextSummary={wizard.agentContext?.contextSummary}
            sampledProducts={wizard.agentContext?.sampledProducts}
            detectedSector={wizard.agentContext?.detectedSector}
            totalLlmCostUsd={wizard.totalLlmCostUsd}
            stepProgress={currentStepData?.output_data?.progress}
          />
        </div>
      </WizardLayout>
    </div>
  );
}
